import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) return { selector: to.hash }
    if (savedPosition) return savedPosition

    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      component: () => import('@/views/app/Index'),
      redirect: '/dashboard',

      children: [
        {
          path: '/dashboard',
          component: () => import('@/views/app/dashboard/Route'),
          children: [
            {
              name: 'DashboardPage',
              path: '',
              component: () => import('@/views/app/dashboard/Dashboard'),
              meta: { onlyAuthUser: true },
            },
          ],
        },
        {
          name: 'NewChannelPage',
          path: '/new-channel',
          component: () => import('@/views/app/channel/NewChannel'),
          meta: { onlyAuthUser: true },
        },
        {
          path: '/channel',
          component: () => import('@/views/app/channel/Route'),
          children: [
            {
              path: ':id/on-air',
              name: 'OnAirPage',
              component: () => import('@/views/app/channel/OnAir'),
              meta: { onlyAuthUser: true },
            },
            {
              path: ':id/edit',
              name: 'ChannelEditPage',
              component: () => import('@/views/app/channel/ChannelEdit'),
              meta: { onlyAuthUser: true },
            },
            {
              path: ':id/scheduler',
              name: 'SchedulerPage',
              component: () => import('@/views/app/scheduler/Scheduler'),
              meta: { onlyAuthUser: true },
            },
            {
              path: ':id/scheduler-manual',
              name: 'ManualSchedulerPage',
              component: () => import('@/views/app/scheduler/SchedulerManual'),
              meta: { onlyAuthUser: true },
            },
            {
              path: ':id/scheduler-settings',
              name: 'SchedulerSettingsPage',
              component: () => import('@/views/app/scheduler/Settings'),
              meta: { onlyAuthUser: true },
            },
            {
              path: ':id/epg',
              name: 'EPG',
              component: () => import('@/views/app/epg/EPG'),
              meta: { onlyAuthUser: true },
            },
            {
              path: ':id/ads',
              name: 'Ads',
              component: () => import('@/views/app/ads/Ads'),
              meta: { onlyAuthUser: true },
            },
            {
              path: ':id/distributions',
              component: () => import('@/views/app/distribution/Route'),
              children: [
                {
                  path: '/',
                  name: 'Distributions',
                  component: () => import('@/views/app/distribution/List'),
                  meta: { onlyAuthUser: true },
                },
                {
                  path: ':distribution_id',
                  name: 'Distribution',
                  component: () => import('@/views/app/distribution/Details'),
                  meta: { onlyAuthUser: true },
                },
              ],
            },
            {
              path: ':id/assets',
              component: () => import('@/views/app/asset/Route'),
              children: [
                {
                  path: '/',
                  name: 'AssetListPage',
                  component: () => import('@/views/app/asset/List'),
                  meta: { onlyAuthUser: true },
                },
                {
                  path: 'edit-meta',
                  name: 'EditMeta',
                  component: () => import('@/views/app/asset/EditMeta'),
                  meta: { onlyAuthUser: true },
                },
                {
                  path: ':asset_id',
                  name: 'AssetDetailPage',
                  component: () => import('@/views/app/asset/Details'),
                  meta: { onlyAuthUser: true },
                },
                {
                  path: ':asset_id/edit',
                  name: 'AssetEditPage',
                  component: () => import('@/views/app/asset/Edit'),
                  meta: { onlyAuthUser: true },
                },
              ],
            },
            {
              path: ':id/media-sources',
              component: () => import('@/views/app/media/Route'),
              children: [
                {
                  path: '/',
                  name: 'MediaSourcesPage',
                  component: () => import('@/views/app/media/Sources'),
                  meta: { onlyAuthUser: true },
                },
                {
                  name: 'AutoSourceNewPage',
                  path: 'new',
                  component: () => import('@/views/app/media/EditAutoSource'),
                  meta: { onlyAuthUser: true },
                },
                {
                  path: ':source_id',
                  name: 'AutoSourceDetailPage',
                  component: () => import('@/views/app/media/AutoSourceDetail'),
                  meta: { onlyAuthUser: true },
                },
                {
                  path: ':source_id/details',
                  name: 'SourceDetailPage',
                  component: () => import('@/views/app/media/SourceDetail'),
                  meta: { onlyAuthUser: true },
                },
                {
                  name: 'AutoSourceEditPage',
                  path: ':source_id/edit',
                  component: () => import('@/views/app/media/EditAutoSource'),
                  meta: { onlyAuthUser: true },
                },
              ],
            },
          ],
        },
        {
          path: '/profile',
          component: () => import('@/views/app/profile/ProfileRoute'),
          children: [
            {
              name: 'ProfileInfoPage',
              path: '',
              component: () => import('@/views/app/profile/ProfilePage'),
              meta: { onlyAuthUser: true },
            },
          ],
        },
      ],
    },
    {
      path: '/signin',
      component: () => import('@/views/app/sessions/Sessions'),
      children: [
        {
          name: 'SignIn',
          path: '',
          component: () => import('@/views/app/sessions/SignIn'),
          meta: { onlyGuestUser: true },
        },
      ],
    },
    {
      path: '/pass-through/:token',
      component: () => import('@/views/app/sessions/PassThrough'),
      name: 'PassThrough',
    },
    {
      path: '*',
      redirect: '/dashboard',
    },
  ],
  data () {
    return {
      prevRoute: null,
    }
  },
})

router.beforeEach((to, from, next) => {
  router.prevRoute = from
  store.dispatch('tryAutoLogin')
    .then(() => {
      const { isAuthenticated } = store.getters

      if (isAuthenticated && to.meta.onlyGuestUser) {
        next({ path: '/' })
      } else if (!isAuthenticated && to.meta.onlyAuthUser) {
        next({ name: 'SignIn' })
      } else {
        if (to.params.id > 0) {
          store.dispatch('getChannelById', to.params.id)
            .catch(() => {
              next({ path: '/' })
            })
        }
        next()
      }
    })
    .catch(() => {
      next({ name: 'SignIn' })
    })
})

export default router
